import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalBody, ModalHeader, ModalFooter, FormGroup, Label, Input } from 'reactstrap';
import ReactLoading from 'react-loading';

const ExtendTimedLotsModal = ({
  isOpen,
  toggle,
  extensionOptions,
  onExtend,
  onRetry,
  error,
  isLoadingExtensions,
  clearState,
}) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedOption('');
      setShowConfirmation(false);
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleProceedToConfirmation = () => {
    if (selectedOption) {
      setShowConfirmation(true);
    }
  };

  const handleCancel = () => {
    setSelectedOption('');
    setShowConfirmation(false);
    clearState();
    toggle();
  };

  const handleConfirmExtend = async () => {
    const option = extensionOptions.find(opt => opt.extensionMinutes === parseInt(selectedOption));
    setIsLoading(true);

    try {
      const result = await onExtend(option.extensionMinutes);

      if (result.success) {
        handleCancel();
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error extending lots:', error);
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setShowConfirmation(false);
  };

  const handleRetry = () => {
    setShowConfirmation(false);
    setSelectedOption('');
    onRetry();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} centered>
      <ModalHeader toggle={handleCancel}>
        {error ? '' : showConfirmation ? 'Are you sure you want to extend timed lots?' : 'Extend Timed Lots'}
      </ModalHeader>
      <ModalBody>
        {error ? (
          <div className="alert alert-danger" role="alert">
            {error.message}
          </div>
        ) : isLoading || isLoadingExtensions ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '150px' }}>
            <ReactLoading type="spin" color="#007bff" height={50} width={50} />
          </div>
        ) : !showConfirmation ? (
          <>
            <p>Unclosed lot end times will be extended by the number of minutes/hours selected below</p>
            <FormGroup>
              <Label for="extensionSelect">Select Extension Time</Label>
              <Input
                type="select"
                name="extensionSelect"
                id="extensionSelect"
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
              >
                <option value="">-- Select an option --</option>
                {extensionOptions.map((option) => (
                  <option key={option.extensionMinutes} value={option.extensionMinutes}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </>
        ) : (
          <p>
            You are about to extend unclosed lot end times by{' '}
            <strong>
              {extensionOptions.find(opt => opt.extensionMinutes === parseInt(selectedOption))?.label}
            </strong>
            . This action cannot be undone.
          </p>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-end border-0">
        {error ? (
          error.type === 'EXTENSION_IN_PROGRESS' || error.type === 'STALE_EXTEND_LOTS_TOKEN' ? (
            <>
              <Button color="link" onClick={handleCancel} disabled={isLoading || isLoadingExtensions}>
                Cancel
              </Button>
              <Button
                className="min-width-max-content mr-3"
                color="primary"
                onClick={handleRetry}
                disabled={isLoading || isLoadingExtensions}
              >
                Retry
              </Button>
            </>
          ) : (
            <Button
              className="min-width-max-content mr-3"
              color="primary"
              onClick={handleCancel}
            >
              Done
            </Button>
          )
        ) : (
          <>
            <Button color="link" onClick={showConfirmation ? handleBack : handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              className="min-width-max-content mr-3"
              color="primary"
              onClick={showConfirmation ? handleConfirmExtend : handleProceedToConfirmation}
              disabled={!selectedOption || isLoading}
            >
              {showConfirmation ? 'Yes' : 'Extend Lots'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ExtendTimedLotsModal;
